import { useEffect, useRef, useState } from 'react';
import { BookOpen, Plus, Trash2, ArrowUp, ArrowDown, ImagePlus, Video, X, Loader2 } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import TipTapEditor from '../TipTapEditor';
import { BARVA_BG, BARVA_BORDER, TYPY_VAZEB, api, nahrajMedium, delka, barva as barvaZnalosti } from './znalosti';

/**
 * Editor článku Znalostí — jen pro administrátora.
 *
 * Jednobodovka se skládá z kroků (nadpis, vysvětlení, fotka) a pevných boxů
 * Tip / Pozor / Hotovo když; návod je volné HTML (Tiptap). Fotky a video jdou
 * do knihovny médií (složka `znalosti`) a článek drží jen jejich id.
 *
 * `vychoziVazby` předvyplní vazbu, když se článek zakládá z kusu zařízení.
 */
export default function ClanekEditor({ isDark, puvodni = null, vychoziTyp = 'jednobodovka', vychoziVazby = [], kategorie = [], povoleneTypy = ['jednobodovka', 'navod'], onClose, onUlozeno }) {
    const akcent = barvaZnalosti(isDark);
    const cara = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const muted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const input = {
        width: '100%', minHeight: 44, borderRadius: 10, padding: '0 12px', outline: 'none', fontSize: 14,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${cara}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    const area = { ...input, padding: 10, minHeight: 70, resize: 'vertical', lineHeight: 1.45 };
    const stitek = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' };

    const [f, setF] = useState(() => ({
        typ: puvodni?.typ || (povoleneTypy.includes(vychoziTyp) ? vychoziTyp : povoleneTypy[0] || vychoziTyp),
        nazev: puvodni?.nazev || '',
        kod: puvodni?.kod || '',
        perex: puvodni?.perex || '',
        kategorie_id: puvodni?.kategorie_id || '',
        obsah_html: puvodni?.obsah_html || '',
        tip: puvodni?.tip || '',
        pozor: puvodni?.pozor || '',
        hotovo_kdyz: puvodni?.hotovo_kdyz || '',
        kriticke: !!puvodni?.kriticke,
        revize_do: puvodni?.revize_do || '',
        stav: puvodni?.stav || 'koncept',
    }));
    const [titulni, setTitulni] = useState(puvodni?.titulni || null);
    const [video, setVideo] = useState(puvodni?.video || null);
    const [kroky, setKroky] = useState(() => (puvodni?.kroky || []).map((k, i) => ({ klic: `k${i}`, nazev: k.nazev, popis: k.popis || '', media: k.media || null })));
    const [vazby, setVazby] = useState(() => (puvodni?.vazby || vychoziVazby).map(v => ({ ...v })));
    const [ciselniky, setCiselniky] = useState(null);
    const [novaVazba, setNovaVazba] = useState({ objekt_typ: 'stranka', hodnota: '' });
    const [nahravam, setNahravam] = useState(null);       // co se právě nahrává ('titulni' | 'video' | klic kroku)
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const souborRef = useRef(null);
    const cilNahrani = useRef(null);

    useEffect(() => { api('/api/znalosti/ciselniky').then(setCiselniky).catch(() => setCiselniky({})); }, []);

    const zmen = (k, v) => setF(s => ({ ...s, [k]: v }));
    const jeOpl = f.typ === 'jednobodovka';

    const vyberSoubor = (cil, accept) => {
        cilNahrani.current = cil;
        souborRef.current.accept = accept;
        souborRef.current.click();
    };
    const poVyberu = async (e) => {
        const soubor = e.target.files?.[0];
        e.target.value = '';
        if (!soubor) return;
        const cil = cilNahrani.current;
        if (soubor.size > 50 * 1024 * 1024) { setChyba('Soubor je větší než 50 MB — zkrať video nebo ho natoč v nižší kvalitě.'); return; }
        setNahravam(cil); setChyba(null);
        try {
            const m = await nahrajMedium(soubor);
            if (cil === 'titulni') setTitulni(m);
            else if (cil === 'video') setVideo(m);
            else setKroky(ks => ks.map(k => (k.klic === cil ? { ...k, media: m } : k)));
        } catch (err) {
            setChyba(err.message);
        } finally {
            setNahravam(null);
        }
    };

    const pridejKrok = () => setKroky(ks => [...ks, { klic: `n${Date.now()}`, nazev: '', popis: '', media: null }]);
    const posun = (i, o) => setKroky(ks => {
        const n = [...ks]; const j = i + o;
        if (j < 0 || j >= n.length) return ks;
        [n[i], n[j]] = [n[j], n[i]];
        return n;
    });

    const moznosti = ciselniky?.[novaVazba.objekt_typ] || [];
    const popisVazby = (v) => {
        if (v.popis) return v.popis;
        const seznam = ciselniky?.[v.objekt_typ] || [];
        const nalez = seznam.find(o => (v.objekt_typ === 'stranka' ? o.klic === v.objekt_klic : o.id === v.objekt_id));
        return nalez?.nazev || (v.objekt_klic ?? `#${v.objekt_id}`);
    };
    const pridejVazbu = () => {
        if (!novaVazba.hodnota) return;
        const jeKlic = novaVazba.objekt_typ === 'stranka';
        const v = { objekt_typ: novaVazba.objekt_typ, objekt_id: jeKlic ? null : Number(novaVazba.hodnota), objekt_klic: jeKlic ? novaVazba.hodnota : null };
        if (vazby.some(x => x.objekt_typ === v.objekt_typ && (jeKlic ? x.objekt_klic === v.objekt_klic : x.objekt_id === v.objekt_id))) return;
        setVazby(vs => [...vs, v]);
        setNovaVazba(s => ({ ...s, hodnota: '' }));
    };

    const ulozit = async (stav) => {
        if (!f.nazev.trim()) { setChyba('Vyplň název'); return; }
        if (jeOpl && kroky.some(k => !k.nazev.trim())) { setChyba('Každý krok potřebuje nadpis'); return; }
        setUkladam(true); setChyba(null);
        const body = {
            ...f,
            stav,
            kod: f.kod || null,
            kategorie_id: f.kategorie_id || null,
            revize_do: f.revize_do || null,
            obsah_html: jeOpl ? null : f.obsah_html,
            titulni_media_id: titulni?.id || null,
            video_media_id: video?.id || null,
            kroky: jeOpl ? kroky.map(k => ({ nazev: k.nazev.trim(), popis: k.popis || null, media_id: k.media?.id || null })) : [],
            vazby: vazby.map(v => ({ objekt_typ: v.objekt_typ, objekt_id: v.objekt_id, objekt_klic: v.objekt_klic })),
        };
        try {
            const ulozeny = await api(puvodni ? `/api/znalosti/${puvodni.id}` : '/api/znalosti', { method: puvodni ? 'PUT' : 'POST', body });
            onUlozeno(ulozeny);
        } catch (e) {
            setChyba(e.message);
            setUkladam(false);
        }
    };

    const tlacMedium = (cil, Icon, text, accept) => (
        <button type="button" onClick={() => vyberSoubor(cil, accept)} disabled={!!nahravam}
            className="flex items-center justify-center"
            style={{ gap: 8, height: 44, padding: '0 14px', borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)', background: 'transparent', border: `1px dashed ${cara}` }}>
            {nahravam === cil ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />} {nahravam === cil ? 'Nahrávám…' : text}
        </button>
    );
    const nahled = (m, onSmazat) => (
        <div className="relative" style={{ width: 96, height: 72, borderRadius: 8, overflow: 'hidden', border: `1px solid ${cara}`, flexShrink: 0, background: '#000' }}>
            {m.typ === 'video'
                ? <div className="grid place-items-center h-full" style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}><Video size={18} />{delka(m.delka_s)}</div>
                : <img src={m.nahled || m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            <button type="button" onClick={onSmazat} aria-label="Odebrat"
                style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: 11, border: 'none', background: 'rgba(0,0,0,0.65)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
                <X size={13} />
            </button>
        </div>
    );

    const paticka = (
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            {chyba && <p style={{ width: '100%', margin: '0 0 4px', fontSize: 13, color: '#EF4444' }}>{chyba}</p>}
            <button type="button" disabled={ukladam} onClick={() => ulozit(f.stav === 'publikovano' ? 'archiv' : 'koncept')}
                style={{ height: 48, flex: 1, borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${cara}` }}>
                {f.stav === 'publikovano' ? 'Do archivu' : 'Uložit koncept'}
            </button>
            <button type="button" disabled={ukladam || !!nahravam} onClick={() => ulozit('publikovano')}
                style={{ height: 48, flex: 1.4, borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer', background: akcent, color: '#fff', border: 'none', opacity: ukladam ? 0.6 : 1 }}>
                {ukladam ? 'Ukládám…' : (f.stav === 'publikovano' ? 'Uložit a publikovat' : 'Publikovat')}
            </button>
        </div>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={puvodni ? 'Upravit článek' : 'Nový článek'} ikona={BookOpen} barva={akcent} sirka={680} paticka={paticka} zavritLze={!ukladam}>
            <input ref={souborRef} type="file" hidden onChange={poVyberu} />
            <div style={{ padding: '4px 2px 8px' }}>
                {!puvodni && povoleneTypy.length > 1 && (
                    <div className="flex" style={{ gap: 8 }}>
                        {[['jednobodovka', 'Jednobodovka', 'Jedna činnost: kroky s fotkou, Pozor, Hotovo když'], ['navod', 'Návod', 'Jak pracovat se systémem: volný text se snímky']].filter(([id]) => povoleneTypy.includes(id)).map(([id, nazev, popis]) => (
                            <button key={id} type="button" onClick={() => zmen('typ', id)} className="flex-1 text-left"
                                style={{ padding: '10px 12px', borderRadius: 12, cursor: 'pointer', background: f.typ === id ? BARVA_BG : 'transparent', border: `1px solid ${f.typ === id ? BARVA_BORDER : cara}`, color: 'var(--text-primary)' }}>
                                <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>{nazev}</span>
                                <span style={{ display: 'block', fontSize: 12, color: muted, marginTop: 2 }}>{popis}</span>
                            </button>
                        ))}
                    </div>
                )}

                <label style={stitek}>Název</label>
                <input style={input} value={f.nazev} onChange={e => zmen('nazev', e.target.value)} placeholder={jeOpl ? 'Výměna oleje ve fritéze' : 'Jak požádat o volno'} maxLength={160} />

                <label style={stitek}>Krátce o čem to je</label>
                <textarea style={area} value={f.perex} onChange={e => zmen('perex', e.target.value)} maxLength={500} placeholder="Jedna dvě věty, ukážou se i v seznamu" />

                <div className="flex flex-wrap" style={{ gap: 10 }}>
                    <div style={{ flex: '1 1 200px' }}>
                        <label style={stitek}>Kategorie</label>
                        <select style={input} value={f.kategorie_id} onChange={e => zmen('kategorie_id', e.target.value ? Number(e.target.value) : '')}>
                            <option value="">Bez kategorie</option>
                            {(ciselniky?.kategorie || kategorie).map(k => <option key={k.id} value={k.id}>{k.nazev}</option>)}
                        </select>
                    </div>
                    {jeOpl && (
                        <div style={{ flex: '0 1 140px' }}>
                            <label style={stitek}>Kód</label>
                            <input style={input} value={f.kod} onChange={e => zmen('kod', e.target.value)} placeholder="automaticky" maxLength={20} />
                        </div>
                    )}
                    <div style={{ flex: '0 1 170px' }}>
                        <label style={stitek}>Revize do</label>
                        <input type="date" style={input} value={f.revize_do || ''} onChange={e => zmen('revize_do', e.target.value)} />
                    </div>
                </div>
                <label className="flex items-center" style={{ gap: 8, marginTop: 12, fontSize: 13.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={f.kriticke} onChange={e => zmen('kriticke', e.target.checked)} style={{ width: 18, height: 18, accentColor: akcent }} />
                    Kritické pro bezpečnost nebo HACCP
                </label>

                <label style={stitek}>Video a titulní fotka</label>
                <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
                    {video ? nahled(video, () => setVideo(null)) : tlacMedium('video', Video, 'Nahrát krátké video', 'video/*')}
                    {titulni ? nahled(titulni, () => setTitulni(null)) : tlacMedium('titulni', ImagePlus, 'Titulní fotka', 'image/*')}
                </div>
                <p style={{ fontSize: 11.5, color: muted, margin: '6px 0 0' }}>Video do 2 minut, nejvýš 50 MB. Natoč ho na výšku přímo u stroje.</p>

                {jeOpl ? (
                    <>
                        <label style={stitek}>Kroky</label>
                        {kroky.map((k, i) => (
                            <div key={k.klic} style={{ padding: 12, borderRadius: 12, border: `1px solid ${cara}`, marginBottom: 10 }}>
                                <div className="flex items-center" style={{ gap: 8 }}>
                                    <span className="grid place-items-center" style={{ width: 26, height: 26, borderRadius: 13, background: BARVA_BG, color: akcent, fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{i + 1}</span>
                                    <input style={{ ...input, minHeight: 40 }} value={k.nazev} placeholder="Co udělat (krátce)" maxLength={160}
                                        onChange={e => setKroky(ks => ks.map(x => (x.klic === k.klic ? { ...x, nazev: e.target.value } : x)))} />
                                    <button type="button" aria-label="Posunout nahoru" onClick={() => posun(i, -1)} disabled={i === 0} style={{ width: 34, height: 34, background: 'transparent', border: 'none', color: muted, cursor: 'pointer', opacity: i === 0 ? 0.3 : 1 }}><ArrowUp size={16} /></button>
                                    <button type="button" aria-label="Posunout dolů" onClick={() => posun(i, 1)} disabled={i === kroky.length - 1} style={{ width: 34, height: 34, background: 'transparent', border: 'none', color: muted, cursor: 'pointer', opacity: i === kroky.length - 1 ? 0.3 : 1 }}><ArrowDown size={16} /></button>
                                    <button type="button" aria-label="Smazat krok" onClick={() => setKroky(ks => ks.filter(x => x.klic !== k.klic))} style={{ width: 34, height: 34, background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer' }}><Trash2 size={16} /></button>
                                </div>
                                <div className="flex" style={{ gap: 10, marginTop: 8 }}>
                                    <textarea style={{ ...area, minHeight: 56 }} value={k.popis} placeholder="Vysvětlení (nepovinné)" maxLength={1000}
                                        onChange={e => setKroky(ks => ks.map(x => (x.klic === k.klic ? { ...x, popis: e.target.value } : x)))} />
                                    {k.media
                                        ? nahled(k.media, () => setKroky(ks => ks.map(x => (x.klic === k.klic ? { ...x, media: null } : x))))
                                        : (
                                            <button type="button" onClick={() => vyberSoubor(k.klic, 'image/*,video/*')} disabled={!!nahravam} aria-label="Přidat fotku kroku"
                                                style={{ width: 96, height: 72, flexShrink: 0, borderRadius: 8, border: `1px dashed ${cara}`, background: 'transparent', color: muted, cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                                                {nahravam === k.klic ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                                            </button>
                                        )}
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={pridejKrok} className="flex items-center justify-center w-full"
                            style={{ gap: 6, height: 44, borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', color: akcent, background: BARVA_BG, border: `1px dashed ${BARVA_BORDER}` }}>
                            <Plus size={16} /> Přidat krok
                        </button>
                    </>
                ) : (
                    <>
                        <label style={stitek}>Obsah návodu</label>
                        <div style={{ borderRadius: 10, border: `1px solid ${cara}`, overflow: 'hidden' }}>
                            <TipTapEditor value={f.obsah_html} onChange={v => zmen('obsah_html', v)} nahratObrazek={async (soubor) => (await nahrajMedium(soubor)).url}
                                placeholder="Postup se snímky obrazovky…" />
                        </div>
                    </>
                )}

                <label style={stitek}>Tip (nepovinné)</label>
                <textarea style={area} value={f.tip} onChange={e => zmen('tip', e.target.value)} maxLength={1000} placeholder="Na co lidé narážejí, zkratka…" />
                <label style={stitek}>Pozor (nepovinné)</label>
                <textarea style={area} value={f.pozor} onChange={e => zmen('pozor', e.target.value)} maxLength={1000} placeholder="Bezpečnost, co se nesmí" />
                <label style={stitek}>Hotovo, když (nepovinné)</label>
                <textarea style={area} value={f.hotovo_kdyz} onChange={e => zmen('hotovo_kdyz', e.target.value)} maxLength={1000} placeholder="Jak poznám, že je to správně" />

                <label style={stitek}>Kde se článek ukáže</label>
                <div className="flex flex-wrap" style={{ gap: 6, marginBottom: 8 }}>
                    {vazby.length === 0 && <span style={{ fontSize: 12.5, color: muted }}>Zatím jen v seznamu Znalostí. Přidej obrazovku, stroj, auto nebo recept.</span>}
                    {vazby.map((v, i) => (
                        <span key={i} className="inline-flex items-center" style={{ gap: 6, height: 30, padding: '0 6px 0 10px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, background: BARVA_BG, border: `1px solid ${BARVA_BORDER}`, color: 'var(--text-primary)' }}>
                            <span style={{ color: muted, fontWeight: 500 }}>{TYPY_VAZEB.find(t => t.id === v.objekt_typ)?.label.split(' (')[0] || v.objekt_typ}:</span> {popisVazby(v)}
                            <button type="button" aria-label="Odebrat vazbu" onClick={() => setVazby(vs => vs.filter((_, j) => j !== i))}
                                style={{ width: 22, height: 22, borderRadius: 11, border: 'none', background: 'transparent', color: muted, cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={13} /></button>
                        </span>
                    ))}
                </div>
                <div className="flex flex-wrap" style={{ gap: 8 }}>
                    <select style={{ ...input, flex: '1 1 180px', width: 'auto' }} value={novaVazba.objekt_typ} onChange={e => setNovaVazba({ objekt_typ: e.target.value, hodnota: '' })}>
                        {TYPY_VAZEB.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                    <select style={{ ...input, flex: '2 1 220px', width: 'auto' }} value={novaVazba.hodnota} onChange={e => setNovaVazba(s => ({ ...s, hodnota: e.target.value }))} disabled={!ciselniky}>
                        <option value="">{ciselniky ? (moznosti.length ? 'Vyber…' : 'Nic k výběru') : 'Načítám…'}</option>
                        {moznosti.map(o => {
                            const hodnota = novaVazba.objekt_typ === 'stranka' ? o.klic : o.id;
                            return <option key={hodnota} value={hodnota}>{o.nazev}</option>;
                        })}
                    </select>
                    <button type="button" onClick={pridejVazbu} disabled={!novaVazba.hodnota}
                        style={{ height: 44, padding: '0 16px', borderRadius: 10, fontWeight: 700, fontSize: 13.5, cursor: 'pointer', background: 'transparent', color: akcent, border: `1px solid ${BARVA_BORDER}`, opacity: novaVazba.hodnota ? 1 : 0.5 }}>
                        Přidat
                    </button>
                </div>
                <p style={{ fontSize: 11.5, color: muted, margin: '6px 0 0' }}>
                    Typ stroje platí pro všechny jeho kusy i pro kroky receptů, které ho používají. Obrazovka = článek nabídne tlačítko ?.
                </p>
            </div>
        </ModalShell>
    );
}
